import React from "react";
import { Brain, Loader2 } from "lucide-react";
import Logo from "./Logo";

const Loading = ({
  type = "spinner",
  size = "md",
  text = "Loading...",
  className = "",
}) => {
  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-6 h-6",
    lg: "w-8 h-8",
    xl: "w-12 h-12",
  };

  const textSizes = {
    sm: "text-sm",
    md: "text-base",
    lg: "text-lg",
    xl: "text-xl",
  };

  if (type === "dots") {
    return (
      <div
        className={`flex items-center justify-center space-x-2 ${className}`}
      >
        <div className="flex space-x-1">
          <div className="w-2 h-2 bg-primary rounded-full animate-bounce [animation-delay:-0.3s]"></div>
          <div className="w-2 h-2 bg-primary rounded-full animate-bounce [animation-delay:-0.15s]"></div>
          <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
        </div>
        {text && (
          <span className={`text-white font-modern ${textSizes[size]} ml-3`}>
            {text}
          </span>
        )}
      </div>
    );
  }

  if (type === "logo") {
    return (
      <div
        className={`flex flex-col items-center justify-center space-y-4 ${className}`}
      >
        <div className="relative">
          <Logo size="h-12 w-12" className="animate-pulse" />
          <div className="absolute inset-0 -m-2">
            <div className="w-16 h-16 border-2 border-primary/30 border-t-primary rounded-full animate-spin"></div>
          </div>
        </div>
        {text && (
          <span
            className={`text-white font-modern ${textSizes[size]} text-center`}
          >
            {text}
          </span>
        )}
      </div>
    );
  }

  if (type === "brain") {
    return (
      <div
        className={`flex items-center justify-center space-x-3 ${className}`}
      >
        <div className="relative">
          <Brain
            className={`${sizeClasses[size]} text-primary animate-pulse`}
          />
          <div className="absolute inset-0">
            <Brain
              className={`${sizeClasses[size]} text-primary/30 animate-ping`}
            />
          </div>
        </div>
        {text && (
          <span className={`text-white font-modern ${textSizes[size]}`}>
            {text}
          </span>
        )}
      </div>
    );
  }

  if (type === "bars") {
    return (
      <div
        className={`flex items-center justify-center space-x-2 ${className}`}
      >
        <div className="flex space-x-1">
          <div className="w-1 h-6 bg-primary rounded-full animate-pulse [animation-delay:0s] [animation-duration:1.4s]"></div>
          <div className="w-1 h-6 bg-primary rounded-full animate-pulse [animation-delay:0.2s] [animation-duration:1.4s]"></div>
          <div className="w-1 h-6 bg-primary rounded-full animate-pulse [animation-delay:0.4s] [animation-duration:1.4s]"></div>
          <div className="w-1 h-6 bg-primary rounded-full animate-pulse [animation-delay:0.6s] [animation-duration:1.4s]"></div>
        </div>
        {text && (
          <span className={`text-white font-modern ${textSizes[size]} ml-3`}>
            {text}
          </span>
        )}
      </div>
    );
  }

  // Default spinner
  return (
    <div className={`flex items-center justify-center space-x-3 ${className}`}>
      <Loader2 className={`${sizeClasses[size]} text-primary animate-spin`} />
      {text && (
        <span className={`text-white font-modern ${textSizes[size]}`}>
          {text}
        </span>
      )}
    </div>
  );
};

// Page Loading Component
export const PageLoading = ({ text = "Loading page..." }) => {
  return (
    <div className="min-h-screen bg-dark-bg flex items-center justify-center">
      <div className="text-center space-y-6">
        <div className="relative">
          <Logo size="h-16 w-16" className="mx-auto animate-pulse" />
          <div className="absolute inset-0 -m-2">
            <div className="w-20 h-20 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto"></div>
          </div>
          <div className="absolute inset-0 -m-4">
            <div className="w-24 h-24 border-4 border-transparent border-r-primary/20 rounded-full animate-spin mx-auto [animation-direction:reverse] [animation-duration:2s]"></div>
          </div>
        </div>
        <div className="space-y-2">
          <h2 className="text-2xl font-display font-semibold text-white">
            HyperLogic
          </h2>
          <p className="text-text-muted font-modern">{text}</p>
        </div>
      </div>
    </div>
  );
};

// Button Loading State
export const ButtonLoading = ({ text = "Processing...", size = "md" }) => {
  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6",
  };

  return (
    <div className="flex items-center space-x-2">
      <div
        className={`${sizeClasses[size]} border-2 border-current border-t-transparent rounded-full animate-spin`}
      ></div>
      <span>{text}</span>
    </div>
  );
};

export default Loading;
